import { useQuery } from "@tanstack/react-query";
import { usage, type QuotaSnapshot, type QuotaWindow } from "../../lib/api";
import { formatClock } from "../../lib/format";
import { Skeleton } from "./Skeleton";

export function UsageMeter() {
  const { data, isLoading } = useQuery({
    queryKey: ["usage", "quota"],
    queryFn: usage.quota,
    refetchInterval: 30_000,
  });

  if (isLoading) {
    return (
      <div className="px-3 py-3 space-y-2">
        <Skeleton height={18} />
        <Skeleton height={18} />
      </div>
    );
  }

  return (
    <div className="px-3 py-3">
      <div className="rounded-md bg-bg-surface border border-border-subtle p-3 space-y-2.5">
        <Header snapshot={data ?? null} />
        <UsageRow label="5-Hour Session" window={data?.five_hour ?? null} />
        <div className="h-px bg-border-subtle" />
        <UsageRow label="7-Day Weekly" window={data?.seven_day ?? null} />
      </div>
    </div>
  );
}

function Header({ snapshot }: { snapshot: QuotaSnapshot | null }) {
  const right = snapshotHint(snapshot);
  return (
    <div className="flex items-baseline justify-between text-caption text-text-tertiary uppercase tracking-wide">
      <span>Usage</span>
      <span className="normal-case tracking-normal text-text-tertiary/80">
        {right}
      </span>
    </div>
  );
}

function snapshotHint(snapshot: QuotaSnapshot | null): string {
  if (!snapshot) return "";
  if (!snapshot.available) return snapshot.reason ?? "unavailable";
  if (snapshot.stale) return `as of ${formatClock(snapshot.fetched_at)}`;
  return "";
}

function UsageRow({
  label,
  window: w,
}: {
  label: string;
  window: QuotaWindow | null;
}) {
  const pct = w ? Math.round(w.utilization * 100) : null;
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_3rem_4rem] items-baseline gap-2 py-1">
      <span className="text-caption text-text-tertiary leading-tight">
        {label}
      </span>
      <span className="text-heading text-text-primary tabular-nums text-right">
        {pct === null ? "—" : `${pct}%`}
      </span>
      <span className="text-mono text-text-tertiary tabular-nums text-right whitespace-nowrap">
        {w ? formatResetIn(w.reset_at) : ""}
      </span>
    </div>
  );
}

function formatResetIn(iso: string): string {
  const ms = Date.parse(iso) - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return "now";
  const totalMinutes = Math.floor(ms / 60_000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  return `${hours}h ${String(minutes).padStart(2, "0")}m`;
}
